import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BarChart3, Brain, Boxes, Cpu, Network, Database, ShieldCheck, Search, LineChart, FileText, Globe, Lightbulb, Wand2, Combine, Package, Target, TrendingUp, Activity, Gauge, Puzzle, MessageSquare, Layers, Image as ImageIcon } from "lucide-react";
import { Reveal, EASE } from "./Reveal";

const SOLUTIONS = [
  {
    id: "enterprise-brain",
    name: "Enterprise Brain",
    icon: Brain,
    headline: "Give AI a shared understanding of your business.",
    subtitle:
      "Build a living enterprise context layer that continuously generates, persists, validates and retrieves business knowledge — giving every AI agent and application trusted context to reason and act.",
    capabilities: [
      { icon: Network, title: "Context Generation", desc: "Continuously discover & create enterprise context from data, business processes, policies, docs & institutional knowledge." },
      { icon: Database, title: "Context Persistence", desc: "Preserve & evolve enterprise context across knowledge graphs, vector stores & memory so intelligence compounds over time." },
      { icon: ShieldCheck, title: "Context Validation", desc: "Continuously assess context for completeness, consistency, conflicts, coverage & freshness to keep AI grounded." },
      { icon: Search, title: "Context Retrieval", desc: "Discover, rank & assemble most relevant context for every query, agent & task, improving through usage & feedback." },
    ],
    demoUrl: "https://app.intugle.ai/#/s/cb293d85-e79c-4b68-8177-ef66fcd518f7/agents_service",
    demoImg: "https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/nrok6y38_image.webp",
    demoDomain: "app.intugle.ai",
    demoLabel: "Enterprise Context Graph",
    note: "Give every agent and application the same trusted understanding of your enterprise.",
    noteLabel: "Unified Business Context",
    capsHeading: "The Enterprise Context Lifecycle Management",
  },
  {
    id: "ai-over-bi",
    name: "AI Over BI",
    icon: BarChart3,
    headline: "Turn Every Business Question into Decision-Ready Intelligence",
    subtitle:
      "Go beyond predefined, static dashboards by combining enterprise data, documents and external information with specialized AI agents that analyze, reason and synthesize deeper, contextual insights for every business question.",
    capabilities: [
      { icon: LineChart, title: "Data Analysis", desc: "Analyze and visualize structured data to uncover trends, anomalies, correlations and business drivers." },
      { icon: FileText, title: "Document Intelligence", desc: "Extract and synthesize insights from reports, contracts, policies and other unstructured enterprise content." },
      { icon: Globe, title: "Web Research", desc: "Enrich internal analysis with relevant market, competitor, industry and third-party intelligence." },
      { icon: Lightbulb, title: "Insight Synthesis", desc: "Combine signals across data, documents and external sources into clear, decision-ready insights." },
    ],
    note: "Move beyond static dashboards to understand what happened, why it happened and what to do next.",
    noteLabel: "Beyond Traditional BI",
    capsHeading: "Specialized AI Agents for Decision Intelligence",
    demoUrl: "https://app.intugle.ai/#/s/39ab5f71-5fdb-4267-9154-9c1695f3237a/agents_service/5364",
    demoImg: "https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/rwfu9wge_image.webp",
    demoDomain: "app.intugle.ai",
    demoLabel: "AI Over BI Agent",
  },
  {
    id: "data-products",
    name: "Data Products",
    icon: Boxes,
    headline: "Productize Enterprise Data for AI and Analytics",
    subtitle:
      "Build production-ready data products faster with AI-assisted transformation, federated access and built-in governance — creating trusted data assets that can be reused across analytics, AI agents and business applications.",
    capabilities: [
      { icon: Wand2, title: "Agentic Data Transformation", desc: "Use agentic product builders and vibe engineering to generate, transform and operationalize ETL/ELT pipelines from business requirements." },
      { icon: Combine, title: "Federated Query Engine", desc: "Query and combine distributed enterprise data across warehouses, lakes and operational systems through a unified federated layer." },
      { icon: ShieldCheck, title: "Data Governance", desc: "Continuously manage data quality, lineage, access controls and security so every product remains governed, traceable and enterprise-ready." },
      { icon: Package, title: "Reusable Data Assets", desc: "Create governed, reusable data products that serve multiple analytics, AI and application use cases while maintaining a consistent source of truth." },
    ],
    note: "Turn fragmented pipelines into governed, reusable data products built for scale.",
    noteLabel: "Beyond Data Pipelines",
    capsHeading: "Build, Govern & Reuse Data Products at Scale",
    demoUrl: "https://app.intugle.ai/#/s/cb293d85-e79c-4b68-8177-ef66fcd518f7/agents_service/5366?product_id=64b6ab59_ce4c_402a_962d_89f046b82e03",
    demoImg: "https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/snvs8fp0_image.webp",
    demoDomain: "app.intugle.ai",
    demoLabel: "Data Product Studio",
  },
  {
    id: "ai-models",
    name: "AI Models",
    icon: Cpu,
    headline: "Build Predictive Intelligence at Agentic Speed",
    subtitle:
      "Use AI agents to accelerate the end-to-end model lifecycle — translating business problems into production-ready predictive, forecasting, anomaly detection and optimization models.",
    capabilities: [
      { icon: Target, title: "Prediction", desc: "Build predictive models that score outcomes, risks and behaviors to support smarter business decisions." },
      { icon: TrendingUp, title: "Forecasting", desc: "Anticipate demand, revenue, inventory and other business metrics using adaptive forecasting models." },
      { icon: Activity, title: "Anomaly Detection", desc: "Continuously identify unusual patterns, deviations and emerging risks across enterprise data." },
      { icon: Gauge, title: "Optimization", desc: "Turn predictions into recommended actions by optimizing decisions against business objectives." },
    ],
    note: "Use AI agents to accelerate model development, validation and production deployment.",
    noteLabel: "Agentic Model Engineering",
    capsHeading: "Agentic Intelligence for Every Decision",
  },
  {
    id: "embedded-intelligence",
    name: "Embedded Intelligence",
    icon: Puzzle,
    headline: "Embed Intelligence Where Business Gets Done",
    subtitle:
      "Bring analytics, enterprise context, AI models and decision intelligence directly into business applications — so users can understand, decide and act without leaving their workflow.",
    capabilities: [
      { icon: MessageSquare, title: "Embedded Conversational AI", desc: "Ask, explore and act through natural-language experiences inside business applications." },
      { icon: Layers, title: "Embedded Context", desc: "Surface relevant enterprise knowledge, metrics, documents and business context dynamically within every user interaction." },
      { icon: Search, title: "Embedded Semantic Search", desc: "Find relevant enterprise data, documents and knowledge based on meaning and context." },
      { icon: Cpu, title: "Embedded AI Models", desc: "Integrate predictions, forecasts, anomaly detection and optimization directly into operational workflows." },
    ],
    note: "Embed conversations, context, models and search directly into the applications people already use.",
    noteLabel: "Intelligence in the Flow",
    capsHeading: "Intelligence Embedded Across Every Experience",
    demoUrl: "https://app.intugle.ai/#/s/39ab5f71-5fdb-4267-9154-9c1695f3237a/chatapp/latest",
    demoImg: "https://customer-assets-rejwkqb3.emergentagent.net/job_intugle-ai-platform/artifacts/q7i2jpk9_image.webp",
    demoPos: "object-top",
    demoDomain: "app.intugle.ai",
    demoLabel: "Embedded Analytics App",
  },
];

const Thumb = ({ s }) => (
  <a
    href={s.demoUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="data-demo-thumb"
    className="group flex flex-col rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)] hover:-translate-y-0.5 transition-all duration-300"
  >
    <div className="shrink-0 flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
      <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
      <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">{s.demoDomain}</span>
    </div>
    <div className="relative aspect-[16/10] overflow-hidden bg-white">
      {s.demoImg ? (
        <img src={s.demoImg} alt={`${s.name} demo application`} loading="lazy" className={`absolute inset-0 w-full h-full object-cover ${s.demoPos || "object-left-top"} transition-transform duration-500 group-hover:scale-[1.03]`} />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#EFE7D9] to-[#E3D6C0] flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.03]">
          <s.icon size={44} strokeWidth={1.2} className="text-[#8A5730]/45" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent transition-opacity duration-300 group-hover:opacity-0" />
      <div className="absolute inset-0 bg-[#211307]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] group-hover:text-[#FFB84D] transition-colors duration-300">{s.demoLabel}</p>
          <p className="text-[15px] font-semibold text-[#3E2818] group-hover:text-white mt-0.5 transition-colors duration-300">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-[#211307] text-white group-hover:bg-white group-hover:text-[#211307] flex items-center justify-center shrink-0 transition-colors duration-300"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </div>
  </a>
);

const Placeholder = ({ s }) => (
  <div className="rounded-xl border border-dashed border-[#8A5730]/35 bg-white/50 aspect-[16/10] flex flex-col items-center justify-center text-center px-6" data-testid="data-solution-visual">
    <div className="w-10 h-10 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><ImageIcon size={18} strokeWidth={1.8} /></div>
    <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730]">{s.name}</p>
    <p className="text-[12px] text-[#3E2818]/45 mt-1.5">Solution visual placeholder</p>
  </div>
);

const Hero = ({ s }) => (
  <div className="grid lg:grid-cols-[1fr_0.8fr] gap-8 lg:gap-10 items-center">
    <div>
      <h3 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight leading-tight">{s.headline}</h3>
      <p className="text-sm md:text-base text-[#3E2818]/65 mt-3 leading-relaxed">{s.subtitle}</p>
    </div>
    {s.demoUrl ? <Thumb s={s} /> : <Placeholder s={s} />}
  </div>
);

const CapStrip = ({ s }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {s.capabilities.map((c) => {
      const title = typeof c === "string" ? c : c.title;
      const desc = typeof c === "string" ? null : c.desc;
      const Icon = typeof c === "string" ? null : c.icon;
      return (
        <div key={title} className="rounded-xl bg-white/65 backdrop-blur-sm border border-white/60 p-4 hover:border-[#8A5730]/35 hover:shadow-[0_18px_44px_-28px_rgba(62,40,24,0.4)] transition-all duration-300" data-testid={`data-capability-${title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
          {Icon && <div className="w-9 h-9 rounded-lg bg-[#8A5730]/10 text-[#8A5730] flex items-center justify-center mb-3"><Icon size={16} strokeWidth={1.9} /></div>}
          <p className="text-[13px] font-semibold text-[#3E2818] leading-snug">{title}</p>
          {desc && <p className="text-[12px] text-[#3E2818]/60 mt-1.5 leading-relaxed">{desc}</p>}
        </div>
      );
    })}
  </div>
);

export const DataSolutions = ({ onContact }) => {
  const [active, setActive] = useState(SOLUTIONS[0].id);
  const s = SOLUTIONS.find((x) => x.id === active);
  return (
    <section id="data-intelligence" className="bg-[#EFE7D9] py-20 lg:py-28" data-testid="data-solutions-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-6xl mb-8">
          <Reveal delay={0.02}>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4" data-testid="data-eyebrow">Data &amp; Intelligence Solutions</p>
          </Reveal>
          <Reveal delay={0.04}>
            <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl" data-testid="data-title">Turn Enterprise Data into Decision Intelligence</h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-4 text-base text-[#3E2818]/65 leading-relaxed max-w-2xl">Build trusted context, intelligent data products, AI models and decision experiences that power agents, applications and business operations across the enterprise.</p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-[0.28fr_1fr] gap-6 lg:gap-12 items-stretch">
          {/* Left rail + supporting note */}
          <Reveal delay={0.1} className="lg:h-full">
            <div className="flex flex-col h-full lg:border-r lg:border-[#3E2818]/12 lg:pr-4">
              <div className="flex flex-col gap-1">
                {SOLUTIONS.map((x) => {
                  const on = x.id === active;
                  return (
                    <button
                      key={x.id}
                      onClick={() => setActive(x.id)}
                      data-testid={`data-rail-${x.id}`}
                      className={`group text-left rounded-lg px-4 py-3 flex items-center gap-3 transition-colors duration-300 ${
                        on ? "bg-white/70" : "hover:bg-white/40"
                      }`}
                    >
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${on ? "bg-[#8A5730] text-white" : "bg-[#3E2818]/5 text-[#3E2818]/45"}`}>
                        <x.icon size={15} strokeWidth={1.9} />
                      </span>
                      <span className={`text-[13.5px] font-semibold transition-colors duration-300 ${on ? "text-[#8A5730]" : "text-[#3E2818]/50 group-hover:text-[#3E2818]/75"}`}>{x.name}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-auto pt-12 px-4">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730] mb-2">{s.noteLabel || "Why it matters"}</p>
                <div className="min-h-[60px]">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={active}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3, ease: EASE }}
                      className="text-[12.5px] text-[#3E2818]/60 leading-relaxed"
                    >
                      {s.note}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Detail: hero + capabilities */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: EASE }}
            >
              <Hero s={s} />
              {s.capsHeading && <p className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-[#8A5730] mt-8 mb-4">{s.capsHeading}</p>}
              <CapStrip s={s} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
